
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BrowserRouter, Link, Outlet, Route, Routes, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/authContext.tsx";
import { AuthCallback } from "./auth/authCallback.tsx";
import { SignInPage } from "./auth/signInPage.tsx";
import { EndpointSelectionPage } from "./auth/endpointSelectionPage.tsx";
import { ErrorBoundary } from "./components/errorBoundary.tsx";
import { FaviconSync } from "./components/faviconSync.tsx";
import { UpdateBanner } from "./components/ui/updateBanner.tsx";
import { ThemeProvider } from "./providers/themeProvider.tsx";
import { RevealCharsLayer } from "./components/revealChars/revealCharsLayer.tsx";
import { ToastProvider } from "./components/ui/toast.tsx";
import { StatusIcon } from "./components/ui/statusIcon.tsx";
import { AlertDialogProvider } from "./hooks/useAlertDialog.tsx";
import { IncompleteDataAlert } from "./components/ui/incompleteDataAlert.tsx";
import { DataGrid, type DataGridHandle } from "./components/dataGrid/dataGrid.tsx";
import { dispatchButtonAction, type ReserveCancelArgs } from "./components/dataGrid/buttonDispatcher.ts";
import type { ButtonClickInfo } from "./components/dataGrid/cellRenderer.tsx";
import type { TransactionOpsArgs } from "./components/transactionOps/transactionOpsModal.tsx";
import type { CurrentRentalsArgs } from "./components/transactionOps/currentRentalsModal.tsx";
import { handleEmailLogButton, type ResendArgs } from "./components/customerEmail/handleEmailLogButton.ts";
import { runStoredProcedureButton } from "./components/dataGrid/gridButtonFallback.ts";
import { updateGridCell } from "./api/endpoints.ts";
import { ParamType } from "./types/grid.ts";
import { useToast } from "./hooks/useToast.ts";
import { useAlertDialog } from "./hooks/useAlertDialog.tsx";
import { SideNav } from "./components/nav/sideNav.tsx";
import { CommandPalette } from "./components/nav/commandPalette.tsx";
import { UserMenu } from "./components/nav/userMenu.tsx";
import { LicenseCounter } from "./components/nav/licenseCounter.tsx";
import { Breadcrumb, resolvePageName } from "./components/nav/breadcrumb.tsx";
import { useRecents } from "./hooks/useRecents.ts";
import { clearPageStateOnNavigation } from "./hooks/useFilterUrlSync.ts";
import { CustomPageRoute } from "./pages/customPageRoute.tsx";
import { ENDPOINT_DASHBOARD_MAP } from "./pages/classicDashboard/classicDashboardPage.tsx";
import { ModuleId } from "./types/session.ts";
import { getStartPage } from "./components/nav/userMenu.tsx";
import { pageRegistry, getPagePath } from "./pages/pageRegistry.ts";
import { useMediaQuery, breakpoints } from "./hooks/useMediaQuery.ts";
import { ToolsProvider, ToolsDrawer, useTools } from "./tools/ToolsDrawer.tsx";
import { SuperAdminPanelProvider } from "./tools/superAdmin/SuperAdminPanelProvider.tsx";
import { SuperAdminPanel, SuperAdminToggle } from "./tools/superAdmin/SuperAdminPanel.tsx";
import { ImpersonationBanner } from "./tools/superAdmin/ImpersonationBanner.tsx";
import { AnnouncementBanner } from "./components/announcement/announcementBanner.tsx";
import { FavoriteButton } from "./components/ui/favoriteButton.tsx";
import { PageHelpButton } from "./components/ui/pageHelpButton.tsx";
import { BrandMark } from "./components/ui/logo.tsx";
import { DropdownMenu, DropdownMenuItem } from "./components/ui/dropdownMenu.tsx";
import { IssaPromptProvider, useIssaPrompts, type IssaPromptTask } from "./pages/issaInsights/issaPromptContext.tsx";
import { useAssistant } from "./config/assistantProfile.ts";
import { getBrandTitle } from "./api/client.ts";
import { BrandSync } from "./config/brandSync.tsx";
import { useBrand } from "./config/useBrand.ts";
import styles from "./app.module.scss";

// Side-effect: register per-grid cell overrides at module load.
import "./pages/statementConfig/statementInvoiceGridOverrides.tsx";
import "./pages/transactionSearch/transactionSearchGridOverrides.tsx";

// Lazy-loaded components
const TransactionOpsModal = lazy(() => import("./components/transactionOps/transactionOpsModal.tsx").then(m => ({ default: m.TransactionOpsModal })));
const PagePopup = lazy(() => import("./components/dataGrid/pagePopup.tsx").then(m => ({ default: m.PagePopup })));
const SimpleKioskPage = lazy(() => import("./pages/simple/simpleKiosk.tsx").then(m => ({ default: m.SimpleKioskPage })));
const SimpleOperatorPage = lazy(() => import("./pages/simple/simpleOperator.tsx").then(m => ({ default: m.SimpleOperatorPage })));
const SimpleTransactionPage = lazy(() => import("./pages/simple/simpleTransaction.tsx").then(m => ({ default: m.SimpleTransactionPage })));
const ClassicDashboardPage = lazy(() => import("./pages/classicDashboard/classicDashboardPage.tsx").then(m => ({ default: m.ClassicDashboardPage })));
const CockpitDashboardPage = lazy(() => import("./pages/cockpitDashboard/cockpitDashboardPage.tsx").then(m => ({ default: m.CockpitDashboardPage })));
const InstantDashboardPage = lazy(() => import("./pages/instantDashboard/instantDashboardPage.tsx").then(m => ({ default: m.InstantDashboardPage })));
const SharedLinkPage = lazy(() => import("./pages/sharedLink/sharedLinkPage.tsx").then(m => ({ default: m.SharedLinkPage })));
const InventoryStartPage = lazy(() => import("./pages/classicDashboard/inventoryStartPage.tsx").then(m => ({ default: m.InventoryStartPage })));
const ProductLockModal = lazy(() => import("./pages/product/productLockModal.tsx").then(m => ({ default: m.ProductLockModal })));
const CurrentRentalsModal = lazy(() => import("./components/transactionOps/currentRentalsModal.tsx").then(m => ({ default: m.CurrentRentalsModal })));
const ResendEmailModal = lazy(() => import("./components/customerEmail/resendEmailModal.tsx").then(m => ({ default: m.ResendEmailModal })));
const KioskTemperatureChartStandalone = lazy(() => import("./pages/kiosk/kioskTemperatureChartStandalone.tsx").then(m => ({ default: m.KioskTemperatureChartStandalone })));

// Standalone temperature charts that overlay door-open events (same as the Edit
// Kiosk temperature tab). Rendered via a dedicated wrapper instead of the plain
// chart grid so the door-opening annotations are wired in.
const TEMPERATURE_DOOR_CHARTS = new Set([
  "kiosktemperaturesensor",
  "kiosktemperaturesensorf",
  "kiosktemperaturemedian",
  "kiosktemperaturemedianf",
]);

function GridPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirm } = useAlertDialog();
  const [txOpsArgs, setTxOpsArgs] = useState<TransactionOpsArgs | null>(null);
  const [popupPath, setPopupPath] = useState<string | null>(null);
  const [productLockId, setProductLockId] = useState<number | null>(null);
  const [currentRentalsArgs, setCurrentRentalsArgs] = useState<CurrentRentalsArgs | null>(null);
  const [resendArgs, setResendArgs] = useState<ResendArgs | null>(null);
  const gridRef = useRef<DataGridHandle | null>(null);
  const name = searchParams.get("name") ?? "";

  // Props for <DataGrid> are memoised so opening a modal (which flips this
  // component's state) doesn't re-render the whole grid before the modal's
  // boot effect can fire — that synchronous re-render was the ~1s click→request
  // stall. DataGrid is wrapped in React.memo; these stable refs let it skip.
  const entry = pageRegistry[name.toLowerCase()];
  const staticFilters = useMemo(
    () => (entry?.kind === "definedGrid" ? (entry.staticFilters ?? []) : []),
    [entry],
  );
  const chartOnly = entry?.kind === "definedGrid" ? (entry.chartOnly ?? false) : false;
  const createPage = entry?.kind === "namedGrid" ? entry.createPage : undefined;
  const autoSearch = entry?.kind === "namedGrid" || entry?.kind === "definedGrid" ? entry.autoSearch ?? false : false;
  const extra = useMemo(
    () => (createPage ? (
      <button className={styles.createBtn} onClick={() => setPopupPath(getPagePath(createPage))}>
        + Create
      </button>
    ) : undefined),
    [createPage],
  );

  const handleReserveCancel = useCallback(async (a: ReserveCancelArgs) => {
    // Guard against a row-less update: with no key filters the Set would hit
    // every row. Bail rather than risk a mass cancel.
    if (a.keyParams.length === 0) {
      toast("Cannot cancel: this grid has no key columns to identify the row", "error");
      return;
    }
    const ok = await confirm({
      title: "Confirm Reservation Cancellation",
      body: a.reservationCode
        ? <>You are about to cancel reservation <b>{a.reservationCode}</b>{a.username ? <> for user <b>{a.username}</b></> : null}.</>
        : "You are about to cancel this reservation.",
      confirmLabel: "Confirm",
      cancelLabel: "Abort",
      destructive: true,
    });
    if (!ok) return;
    try {
      const result = await updateGridCell(name, [
        ...a.keyParams,
        { sColumn: a.setColumn, oValue: a.setValue, Type: ParamType.Set },
      ]);
      if (!result.bError) {
        toast("Reservation canceled", "success");
        gridRef.current?.refresh();
      } else {
        toast(result.sMessage || "Failed to cancel reservation", "error");
      }
    } catch {
      toast("Failed to cancel reservation", "error");
    }
  }, [name, confirm, toast]);

  const handleButtonClick = useCallback(
    (info: ButtonClickInfo, row: unknown[], colIndex: Record<string, number>, keyColumns: string[]) => {
      // Email-log buttons (View Email = 112; Resend = 20/21 + pageName "239")
      // reuse the shared handler + ResendEmailModal.
      if (info.type === 112 || ((info.type === 20 || info.type === 21) && info.pageName === "239")) {
        handleEmailLogButton(info, row, colIndex, toast, setResendArgs);
        return;
      }
      const handled = dispatchButtonAction(info, row, colIndex, navigate, {
        onTransactionOps: setTxOpsArgs,
        onPopup: setPopupPath,
        onProductLock: (productId) => setProductLockId(productId),
        onCurrentRentals: setCurrentRentalsArgs,
        onReserveCancel: handleReserveCancel,
      }, keyColumns);
      if (handled) return;
      // SP-exec fallback (shared with genericGridPage): confirm-on-destructive,
      // skips non-procedure page names, refreshes on success.
      void runStoredProcedureButton(info, {
        confirm,
        notify: (m, k) => toast(m, k),
        refresh: () => gridRef.current?.refresh(),
      }).then((ran) => {
        if (!ran) console.warn("[GridPage] unhandled grid button", { type: info.type, pageName: info.pageName, label: info.label });
      });
    },
    [navigate, handleReserveCancel, confirm, toast],
  );

  if (!name) return <div>Missing ?name parameter</div>;
  return (
    <>
      {TEMPERATURE_DOOR_CHARTS.has(name.toLowerCase()) ? (
        <Suspense fallback={null}>
          <KioskTemperatureChartStandalone name={name} staticFilters={staticFilters} />
          </Suspense>
      ) : (
        <DataGrid
          ref={gridRef}
          key={name}
          name={name}
          staticFilters={staticFilters}
          chartOnly={chartOnly}
          autoSearch={autoSearch}
          toolbarExtra={extra}
          onButtonClick={handleButtonClick}
        />
      )}
      {txOpsArgs && (
        <Suspense fallback={null}>
          <TransactionOpsModal
            open={!!txOpsArgs}
            args={txOpsArgs}
            onClose={() => setTxOpsArgs(null)}
          />
        </Suspense>
      )}
      {popupPath && (
        <Suspense fallback={null}>
          <PagePopup path={popupPath} onClose={(didChange) => { setPopupPath(null); if (didChange) gridRef.current?.refresh(); }} />
        </Suspense>
      )}
      {productLockId != null && (
        <Suspense fallback={null}>
          <ProductLockModal
            open
            productId={productLockId}
            onClose={() => setProductLockId(null)}
          />
        </Suspense>
      )}
      {currentRentalsArgs && (
        <Suspense fallback={null}>
          <CurrentRentalsModal
            args={currentRentalsArgs}
            onClose={() => setCurrentRentalsArgs(null)}
            onOpenRefund={(a) => { setCurrentRentalsArgs(null); setTxOpsArgs(a); }}
          />
        </Suspense>
      )}
      {resendArgs && (
        <Suspense fallback={null}>
          <ResendEmailModal
            open
            emailLogId={resendArgs.emailLogId}
            transactionId={resendArgs.transactionId}
            customerEmail={resendArgs.customerEmail}
            onClose={() => setResendArgs(null)}
          />
        </Suspense>
      )}
    </>
  );
}

function ToolsButton() {
  const { openTools } = useTools();
  return (
    <button
      onClick={() => openTools()}
      className={styles.iconBtn}
      title="Tools"
      aria-label="Open tools"
    >
      <StatusIcon kind="wrench" size={18} />
    </button>
  );
}

function IssaButton() {
  const navigate = useNavigate();
  const { cache } = useAuth();
  const { unreadTasks, markRead } = useIssaPrompts();
  const assistant = useAssistant();
  // Hide for Ingress-only users; show for everyone else
  if (cache?.CommonData?.IngressMembership) return null;
  const openAnswer = (task: IssaPromptTask) => {
    markRead(task.id);
    navigate("/page/issainsights", {
      state: { conversationGuid: task.conversationGuid, model: task.model },
    });
  };
  const trigger = (
    <button
      onClick={() => {
        if (unreadTasks.length === 1) openAnswer(unreadTasks[0]!);
        else if (unreadTasks.length === 0) navigate("/page/issainsights");
      }}
      className={styles.issaBtn}
      title={`Chat with ${assistant.name}`}
      aria-label={`Chat with ${assistant.name}`}
    >
      <img src={assistant.face} alt="" width="22" height="22" style={{ borderRadius: "50%" }} />
      <span className={styles.issaBtnText}>Chat with {assistant.name}</span>
      {unreadTasks.length > 0 && (
        <span className={styles.issaNotificationBadge} aria-label={`${unreadTasks.length} ${assistant.name} ${unreadTasks.length === 1 ? "answer" : "answers"} ready`}>
          {unreadTasks.length > 9 ? "9+" : unreadTasks.length}
        </span>
      )}
    </button>
  );
  if (unreadTasks.length <= 1) return trigger;
  return (
    <DropdownMenu trigger={trigger} align="right">
      {unreadTasks.map((task) => (
        <DropdownMenuItem key={task.id} onClick={() => openAnswer(task)}>
          <span className={styles.issaNotificationItem}>
            <span className={styles.issaNotificationTitle}>{task.result?.Title || task.displayPrompt || task.prompt}</span>
            <span className={styles.issaNotificationMeta}>Answer ready</span>
          </span>
        </DropdownMenuItem>
      ))}
    </DropdownMenu>
  );
}

function MainLayout() {
  const [navOpen, setNavOpen] = useState(false);
  const isDesktop = useMediaQuery(breakpoints.md);
  const isLg = useMediaQuery(breakpoints.lg);
  // Rail width matches sideNav's .navRail (56 px). Full desktop width is 232 px.
  const desktopNavWidth = isLg ? 232 : 56;
  const location = useLocation();

  // Auto-close sidebar on route change (mobile)
  useEffect(() => {
    if (!isDesktop) setNavOpen(false);
  }, [location.pathname, location.search, isDesktop]);

  // Clear page state blob when navigating to a different page.
  // State only persists for reload, not for SPA navigation.
  //
  // "Different page" = pathname changed OR grid name changed (for /grid?name=X
  // routes). Row/edit selection lives in the page state blob now, not the URL,
  // so changing selection won't trigger a clear.
  const firstPageRef = useRef(true);
  const lastPathnameRef = useRef(location.pathname);
  const lastGridQueryRef = useRef<string | null>(new URLSearchParams(location.search).get("name"));
  useEffect(() => {
    const currentGridName = new URLSearchParams(location.search).get("name");
    if (firstPageRef.current) {
      firstPageRef.current = false;
      lastPathnameRef.current = location.pathname;
      lastGridQueryRef.current = currentGridName;
      return;
    }
    const pathChanged = location.pathname !== lastPathnameRef.current;
    const gridChanged = currentGridName !== lastGridQueryRef.current;
    if (pathChanged || gridChanged) {
      lastPathnameRef.current = location.pathname;
      lastGridQueryRef.current = currentGridName;
      clearPageStateOnNavigation();
    }
  }, [location.pathname, location.search]);

  const closeNav = useCallback(() => setNavOpen(false), []);

  const [paletteOpen, setPaletteOpen] = useState(false);

  // Global shortcuts — keep on Ctrl+Shift+<key> convention (see AGENTS.md § Keyboard Shortcuts)
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (!e.ctrlKey || !e.shiftKey) return;
      if (e.code === "Space") {
        e.preventDefault();
        e.stopImmediatePropagation();
        setPaletteOpen(true);
        return;
      }
      const key = e.key.toUpperCase();
      if (key === "F") {
        e.preventDefault();
        setNavOpen(true);
        requestAnimationFrame(() => {
          const input = document.getElementById("sidenav-search") as HTMLInputElement | null;
          input?.focus();
          input?.select();
        });
      }
      // Ctrl+Shift+Å — hide header + breadcrumb bar (dev shortcut, refresh to restore)
      if (e.key === "å" || e.key === "Å") {
        e.preventDefault();
        document.querySelector("header")?.remove();
        document.querySelector("[class*='topBarContext']")?.remove();
      }
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  // Dynamic page title
  const { cache } = useAuth();
  const brandTitle = useBrand().title;
  const pageName = resolvePageName(location.pathname, location.search);
  const pageDisplayName = useMemo(() => {
    if (!pageName || !cache?.PageInfo?.Page) return null;
    const page = cache.PageInfo.Page.find(p => p.Name.toLowerCase() === pageName.toLowerCase());
    return page?.DisplayName ?? null;
  }, [pageName, cache?.PageInfo?.Page]);

  useEffect(() => {
    document.title = pageDisplayName ? `${brandTitle} - ${pageDisplayName}` : brandTitle;
    // Signing out drops back to the host's own product name — the tab must not
    // keep advertising the tenant you just left.
    return () => { document.title = getBrandTitle(); };
  }, [brandTitle, pageDisplayName]);

  // Record page visits for the Recents sidenav row.
  // Skip the start page (root "/"): it has its own nav entry above Recents, and
  // its synthetic "dashboard" name collides with the Dashboard grid — so the
  // Recents link would mis-route to /grid?name=dashboard instead of the start page.
  const { recordRecent } = useRecents();
  useEffect(() => {
    if (pageName && location.pathname !== "/") recordRecent(pageName);
  }, [pageName, location.pathname, recordRecent]);

  return (
    <div className={styles.appShell}>
      <ImpersonationBanner />
      <AnnouncementBanner />
      <header className={styles.topBarMain}>
        <div className={styles.leftIcons}>
          <Link to="/" className={styles.homeBtn} title="Dashboard" aria-label="Dashboard">
            <BrandMark className={styles.brandLogo} />
          </Link>
          <button
            onClick={() => setNavOpen((v) => !v)}
            className={styles.menuToggle}
            title={navOpen ? "Collapse menu" : "Expand menu"}
            aria-label={navOpen ? "Collapse menu" : "Expand menu"}
          >
            <StatusIcon kind="menu" size={18} />
          </button>
        </div>
        <span className={styles.topBarBrand}>{brandTitle}</span>
        
        <span className={styles.topBarSpacer} aria-hidden="true" />
        <LicenseCounter />
        <IssaButton />
        <SuperAdminToggle />
        <ToolsButton />
        <UserMenu />
      </header>
      <div className={styles.topBarContext}>
        <span className={styles.breadcrumbWrap}>
          <Breadcrumb />
          <FavoriteButton />
          <PageHelpButton />
        </span>
      </div>
      <div className={styles.bodyRow}>
        {/* Mobile overlay nav */}
        {!isDesktop && navOpen && (
          <div className={styles.navOverlay} onClick={closeNav} />
        )}
        <div
          className={`${styles.navPanel} ${!isDesktop && navOpen ? styles.navPanelMobileOpen : ""}`}
          style={isDesktop ? {
            width: navOpen ? desktopNavWidth : 0,
            minWidth: navOpen ? desktopNavWidth : 0,
          } : undefined}
        >
          <SideNav />
        </div>
        <main className={styles.main}>
          <Outlet />
        </main>
      </div>
      <ToolsDrawer />
      <SuperAdminPanel />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}

/** Module IDs that use a custom start page instead of the widget dashboard. */
const MODULE_STARTPAGE_MAP: Record<number, React.ReactElement> = {
  [ModuleId.InstantInventory]: <InventoryStartPage />,
};

function StartPageRoute() {
  const { session, cache, canRead, isSuperAdmin, activeEndpoint } = useAuth();

  // Module-level override (e.g. Instant Inventory → logo-only page)
  const moduleOverride = activeEndpoint?.ModuleID
    ? MODULE_STARTPAGE_MAP[activeEndpoint.ModuleID]
    : undefined;
  if (moduleOverride) return <Suspense fallback={null}>{moduleOverride}</Suspense>;

  const instantPage = session?.ActiveEndPointID
    ? ENDPOINT_DASHBOARD_MAP[session.ActiveEndPointID]
    : undefined;

  const hasCockpitAccess = useMemo(() => {
    if (isSuperAdmin) return true;
    const page = cache?.PageInfo?.Page?.find(p => p.Name?.toLowerCase() === "cockpitdashboard");
    return page ? canRead(page.RowID) : false;
  }, [cache, canRead, isSuperAdmin]);

  const pref = getStartPage(session?.UID, session?.ActiveEndPointID);
  const showCockpit = pref === "new" && hasCockpitAccess;
  const dashboard = instantPage
    ? <InstantDashboardPage pageName={instantPage} />
    : showCockpit ? <CockpitDashboardPage /> : <ClassicDashboardPage />;

  return <Suspense fallback={null}>{dashboard}</Suspense>;
}

function AppShell() {
  const { phase } = useAuth();
  const location = useLocation();
  const isSharedLink = /^\/s\/[a-f0-9]{32}$/i.test(location.pathname);

  switch (phase) {
    case "validating":
      return (
        <div className={styles.loadingScreen}>
          <div className={styles.loadingSpinner} />
        </div>
      );
    case "signed-out":
      return <SignInPage />;
    case "picking-endpoint": {
      if (isSharedLink) {
        return <Suspense fallback={null}><SharedLinkPage /></Suspense>;
      }
      return <EndpointSelectionPage />;
    }
    case "loading-cache":
      return (
        <div className={styles.loadingScreen}>
          <div className={styles.loadingSpinner} />
        </div>
      );
    case "ready":
      return (
        <SuperAdminPanelProvider>
          <ToolsProvider>
            <Routes>
              <Route path="s/:id" element={<Suspense fallback={null}><SharedLinkPage /></Suspense>} />
              <Route path="/" element={<MainLayout />}>
                <Route
                  index
                  element={<StartPageRoute />}
                />
                <Route path="grid" element={<GridPage />} />
                <Route path="simple/kiosk" element={<Suspense fallback={null}><SimpleKioskPage /></Suspense>} />
                <Route path="simple/operator" element={<Suspense fallback={null}><SimpleOperatorPage /></Suspense>} />
                <Route path="simple/transaction" element={<Suspense fallback={null}><SimpleTransactionPage /></Suspense>} />
                <Route path="page/:name" element={<CustomPageRoute />} />
              </Route>
            </Routes>
          </ToolsProvider>
        </SuperAdminPanelProvider>
      );
  }
}

export function App() {
  return (
    <ErrorBoundary>
      <ThemeProvider>
        <FaviconSync />
        <RevealCharsLayer />
        <UpdateBanner />
        <ToastProvider>
          <AlertDialogProvider>
            <IncompleteDataAlert />
            <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/+$/, "")}>
              <AuthProvider>
                <BrandSync />
                <IssaPromptProvider>
                  <Routes>
                    <Route path="/auth/callback" element={<AuthCallback />} />
                    <Route path="/*" element={<AppShell />} />
                  </Routes>
                </IssaPromptProvider>
              </AuthProvider>
            </BrowserRouter>
          </AlertDialogProvider>
        </ToastProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}
